import { useCallback, useEffect } from 'react'
import { useRouter } from 'next/router'
import {
  getUserPseudoToken,
  identifyUser as identifyUserService
} from '@api/fit'
import { getCustomerFit } from '@api/fit'
import { useFit } from '@helpers/hooks/useFit'
import trackError from '@helpers/trackError'
import { getUserPseudoTokenCookie } from '@helpers/session/user'
import { COOKIE_EXPIRE_1_YEAR } from '@consts/index'
import { setCookie } from '@helpers/session'
import { identify, analytics } from '@helpers/analytics'
import { getCustomerId } from '@helpers/session/user'
import { Fit } from 'types/fit'
import { Customer } from '@components/account/types'
import { decode } from '@helpers/shopify'
import { identifyOpenAiCustomer } from '@helpers/openaiAds/pixel'

function getFitDataStatus(
  fitFromDatabase: Fit | undefined,
  savedFitFromCookies: Fit | null
) {
  if (!savedFitFromCookies) {
    return 'empty'
  }

  const fitSourcesMatchContents =
    Object.entries(savedFitFromCookies).sort().toString() ===
    Object.entries(fitFromDatabase || {})
      .sort()
      .toString()

  return fitSourcesMatchContents ? 'match' : 'mismatch'
}

async function identifyUser(userId: string, pseudoToken: string) {
  try {
    await identifyUserService({
      token: pseudoToken,
      customer_id: userId
    })
  } catch (error) {
    trackError(new Error('User could not be identified'), {
      error: error as Error,
      section: 'IdentifyUser'
    })
  }
}

const getCustomerToken = async () => {
  const userCookiePseudoToken = getUserPseudoTokenCookie()
  if (userCookiePseudoToken) return userCookiePseudoToken

  const pseudoToken = await getUserPseudoToken()
  if (pseudoToken) {
    setCookie('token', pseudoToken, COOKIE_EXPIRE_1_YEAR)
  }
  return pseudoToken
}

const fetchCustomerFit = async (
  userId: string,
  savedFitFromCookies: Fit | null
): Promise<{
  customerFit?: Fit
  email?: string
  updatedAt?: string
}> => {
  try {
    const {
      fit,
      email,
      updated_at: updatedAt
    } = await getCustomerFit({ customerId: userId })
    const customerFit =
      fit?.top && fit?.bottom ? { ...fit.top, ...fit.bottom } : undefined

    analytics('User Fit Data Fetched', {
      fit_cookies: getFitDataStatus(customerFit, savedFitFromCookies)
    })

    return { customerFit, email, updatedAt }
  } catch (error) {
    trackError(error as Error, { section: 'FetchCustomerFit' })
    return {}
  }
}

export default function useCustomer(userProfile?: Customer | null): {
  userId: string | undefined
} {
  const router = useRouter()
  const { savedFit, applySavedFit } = useFit()
  const customerIdFromCookie = getCustomerId()
  const customerId = userProfile?.id && decode(userProfile?.id)?.id
  const userId =
    customerId || (router.query?.userId as string) || customerIdFromCookie

  const initCustomer = useCallback(async () => {
    try {
      if (customerId) {
        identifyOpenAiCustomer({
          externalId: customerId,
          email: userProfile?.emailAddress?.emailAddress,
          phone: userProfile?.phoneNumber?.phoneNumber,
          firstName: userProfile?.firstName,
          lastName: userProfile?.lastName
        })
      }
      const pseudoToken = await getCustomerToken()

      if (customerId) {
        identify(customerId, {
          email: userProfile?.emailAddress?.emailAddress,
          firstName: userProfile?.firstName,
          lastName: userProfile?.lastName
        })
      }

      if (userId && pseudoToken) {
        await identifyUser(userId, pseudoToken)
      }

      if (!userId) return

      setCookie('customerId', userId, 0)
      const { customerFit, email, updatedAt } = await fetchCustomerFit(
        userId,
        savedFit
      )

      if (customerFit) {
        identify(userId, { ...customerFit, email })
        applySavedFit(customerFit, updatedAt)
      }
    } catch (error) {
      trackError(error as Error, { section: 'InitCustomer' })
    }
  }, [userId, userProfile])

  useEffect(() => {
    initCustomer()
  }, [initCustomer])

  return { userId }
}
